<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      class="profile-modal-overlay"
      :class="{ 'dragging-active': !isInitialPosition }"
    >
      <div
        ref="modalRef"
        class="profile-modal"
        :class="{ 'is-dragging': isDragging }"
        :style="modalStyle"
        @click.stop
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <div class="profile-header" :class="{ draggable: true }">
          <div class="profile-header-content">
            <div class="profile-header-top">
              <Icon icon="mdi:linkedin" width="16" height="16" class="header-icon" />
              <h4 class="profile-title">LinkedIn Company Profile</h4>
            </div>
          </div>
          <div class="profile-header-actions">
            <div class="drag-handle" title="Drag to move">
              <Icon icon="mdi:drag" width="16" height="16" />
            </div>
            <button type="button" class="btn-close-profile" @click="close">
              <Icon icon="mdi:close" width="18" height="18" />
            </button>
          </div>
        </div>
        <div class="profile-body">
          <CompanyProfileTab
            :profile="transformedProfile"
            :loading="companyProfileLoading"
            :error="companyProfileError"
            @retry="loadCompanyProfile"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { Icon } from "@iconify/vue";
import CompanyProfileTab from "@/components/bucket/company/CompanyProfileTab.vue";
import Unipile from "@/services/unipile/unipile";
import CompanyService from "@/services/company/company";

interface Props {
  show: boolean;
  selectedBucket?: {
    contact?: {
      company?: {
        linkedInUrl?: string;
        name?: string;
        companyId?: string;
        logoUrl?: string | null;
      };
    };
  } | null;
  linkedInChannel?: {
    accountId?: string;
    status?: string;
  } | null;
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  selectedBucket: null,
  linkedInChannel: null,
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "logo-saved", url: string): void;
}>();

// Dragging state
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

// Profile state
const companyProfile = ref<any>(null);
const companyProfileLoading = ref(false);
const companyProfileError = ref<string | null>(null);
const companyProfileCache = ref<Record<string, any>>({});

// Dynamic positioning for drag functionality
const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};

  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
    width: "28%",
    minWidth: "350px",
    maxWidth: "500px",
    minHeight: "60vh",
    maxHeight: "85vh",
    borderRadius: "12px !important",
    zIndex: "10060",
  };
});

// Transform company profile data
const transformedProfile = computed(() => {
  if (!companyProfile.value) return null;

  const profile = companyProfile.value;
  return {
    companyName: profile.name || "",
    description: profile.description || "",
    industry: profile.industry || "",
    website: profile.website || "",
    companyType: profile.company_type || "",
    employeeCount: profile.employee_count || null,
    headquarters: profile.headquarters || "",
    specialties: profile.specialties || [],
    foundedYear: profile.founded_year || null,
    logoUrl: profile.logo_large || profile.logo || "",
    coverImageUrl: profile.cover_image_url || "",
    followersCount: profile.followers_count || null,
  };
});

// Drag handlers
const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [
    ".btn-close-profile",
    ".profile-body",
    ".company-profile-tab",
    ".profile-card",
    "a",
    "button",
    "input",
    "textarea",
  ];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 480) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  const dragEvents = [
    ["mousemove", handleDrag, { passive: false }],
    ["mouseup", stopDrag],
    ["touchmove", handleDrag, { passive: false }],
    ["touchend", stopDrag],
  ] as const;

  dragEvents.forEach(([event, handler, options]) => {
    document.addEventListener(event, handler as any, options);
  });
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;

  const dragEvents = ["mousemove", "mouseup", "touchmove", "touchend"];
  const handlers = [handleDrag, stopDrag, handleDrag, stopDrag];

  dragEvents.forEach((event, index) => {
    document.removeEventListener(event, handlers[index] as any);
  });
};

// Helper functions
const isChannelHealthyStatus = (status?: string): boolean => {
  if (!status) return false;
  const s = String(status).toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(s);
};

const extractCompanyIdentifierFromUrl = (url?: string): string | null => {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    // Match company URLs: /company/company-name
    const companyMatch = path.match(/\/company\/([^/]+)/i);
    if (companyMatch?.[1]) return decodeURIComponent(companyMatch[1]);

    // Fallback: try to extract from path
    const parts = path.split("/").filter(Boolean);
    if (parts.length === 0) return null;
    return decodeURIComponent(parts[parts.length - 1]);
  } catch (error) {
    console.error("Failed to parse company LinkedIn URL:", error);
    return null;
  }
};

// Load company profile
const loadCompanyProfile = async () => {
  const companyLinkedInUrl = props.selectedBucket?.contact?.company?.linkedInUrl;
  if (!companyLinkedInUrl) {
    companyProfileError.value = "Please add a LinkedIn URL to the company first.";
    companyProfile.value = null;
    return;
  }

  if (!props.linkedInChannel?.accountId) {
    companyProfileError.value = "LinkedIn channel is not connected.";
    companyProfile.value = null;
    return;
  }

  if (!isChannelHealthyStatus(props.linkedInChannel?.status)) {
    companyProfileError.value = "LinkedIn channel is disconnected. Please reconnect first.";
    companyProfile.value = null;
    return;
  }

  const identifier = extractCompanyIdentifierFromUrl(companyLinkedInUrl);
  if (!identifier) {
    companyProfileError.value = "Invalid company LinkedIn URL. Please update the company.";
    companyProfile.value = null;
    return;
  }

  const cacheKey = `${identifier}-${props.linkedInChannel.accountId}`;
  if (companyProfileCache.value[cacheKey]) {
    companyProfile.value = companyProfileCache.value[cacheKey];
    companyProfileError.value = null;
    return;
  }

  try {
    companyProfileLoading.value = true;
    companyProfileError.value = null;

    const response = await Unipile.getCompanyProfile(identifier, {
      account_id: props.linkedInChannel.accountId,
    });

    const profileData = response?.data?.data;
    if (!profileData) {
      companyProfileError.value = "Company profile data not available.";
      companyProfile.value = null;
      return;
    }

    companyProfileCache.value[cacheKey] = profileData;
    companyProfile.value = profileData;
    companyProfileError.value = null;

    const companyId = props.selectedBucket?.contact?.company?.companyId;
    const alreadyHasLogo = !!props.selectedBucket?.contact?.company?.logoUrl;
    const logoUrl = profileData.logo_large || profileData.logo;
    if (companyId && !alreadyHasLogo && logoUrl) {
      saveCompanyLogo(companyId, logoUrl);
    }
  } catch (error: any) {
    console.error("Failed to load company profile:", error);
    companyProfileLoading.value = false;

    const statusCode = error?.response?.status;
    const errorData = error?.response?.data;

    const getErrorMessage = () => {
      if (errorData?.providerError?.detail) return errorData.providerError.detail;
      if (errorData?.error?.body?.detail) return errorData.error.body.detail;
      if (errorData?.errorMessage) return errorData.errorMessage;
      if (errorData?.message) return errorData.message;
      if (errorData?.providerError?.title) return errorData.providerError.title;
      if (error?.message) return error.message;
      return null;
    };

    const extractedMessage = getErrorMessage();
    let errorMessage = "Failed to load company profile.";

    switch (statusCode) {
      case 400:
        errorMessage =
          extractedMessage || "Invalid request. Please check the company LinkedIn URL.";
        break;
      case 401:
        errorMessage =
          extractedMessage ||
          "Your LinkedIn account appears to be disconnected. Please reconnect your account.";
        break;
      case 403:
        errorMessage =
          extractedMessage ||
          "This feature is not available for your subscription or account type.";
        break;
      case 404:
        errorMessage =
          extractedMessage || "Company not found. The requested company was not found.";
        break;
      case 422:
        errorMessage = extractedMessage || "Invalid account for this feature.";
        break;
      case 429:
        errorMessage = extractedMessage || "Too many requests. Please wait a moment and try again.";
        break;
      case 500:
      case 503:
      case 504:
        errorMessage =
          extractedMessage ||
          "LinkedIn service is temporarily unavailable. Please try again later.";
        break;
      default:
        errorMessage =
          extractedMessage || error?.message || "An unexpected error occurred. Please try again.";
    }

    companyProfileError.value = errorMessage;
    companyProfile.value = null;
  } finally {
    companyProfileLoading.value = false;
  }
};

const saveCompanyLogo = async (companyId: string, logoUrl: string) => {
  try {
    const response = await CompanyService.saveCompanyLogo(companyId, logoUrl);
    if (response.data.status) {
      emit("logo-saved", response.data.data.logoUrl);
    }
  } catch {
    // Silent failure — logo is non-critical
  }
};

const close = () => {
  isInitialPosition.value = true;
  modalPosition.value = { x: 0, y: 0 };
  emit("close");
};

// Watch for show prop changes
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      loadCompanyProfile();
    } else {
      companyProfileError.value = null;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
});
</script>

<style scoped>
.profile-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  /* Sits to the left of LinkedInProfileViewerModal's default position (which
     uses padding-right: 32px, max-width: 500px) so the two can be open at
     once, side by side, without overlapping. */
  padding-right: 540px;
  padding-top: 16px;
  padding-bottom: 16px;
  z-index: 10060;
  pointer-events: none;
}

.profile-modal-overlay.dragging-active {
  align-items: flex-start;
  justify-content: flex-end;
}

.profile-modal {
  background: #ffffff;
  border-radius: 12px !important;
  width: 28%;
  min-width: 350px;
  max-width: 500px;
  min-height: 60vh;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transition: box-shadow 0.2s ease;
  user-select: none;
  pointer-events: auto;
  position: relative;
}

.profile-modal.is-dragging {
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  cursor: grabbing;
  transition: none;
  border-radius: 12px !important;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 14px 16px;
  border-bottom: 1px solid #e8e8e8;
  transition: background-color 0.2s ease;
}

.profile-header.draggable {
  cursor: grab;
  user-select: none;
  transition: background-color 0.2s ease;
}

.profile-header.draggable:hover {
  background-color: #f8f9fa;
}

.profile-header.draggable:active {
  cursor: grabbing;
  background-color: #e9ecef;
}

.profile-header-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.profile-header-top {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-icon {
  color: #0077b5;
}

.profile-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.profile-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-handle {
  color: #9ca3af;
  cursor: grab;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-handle:hover {
  color: #6b7280;
  background-color: #f3f4f6;
}

.btn-close-profile {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close-profile:hover {
  color: #374151;
}

.profile-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
